/**
 * One QueryClient for the whole app, plus the shared key factory.
 *
 * Defaults are tuned for an operator console: data is fresh enough to trust,
 * nothing refetches just because a window regained focus, and a 4xx is never
 * retried because the server already gave its final answer (a quota denial and
 * a suppression block are answers, not faults).
 *
 * Every cache key starts with a domain segment from `qk`. That is what makes
 * "switch brand, then invalidate everything except the session" a one-liner.
 */
import { QueryClient } from '@tanstack/react-query'
import { isApiError } from './api'

const RETRYABLE_STATUSES = new Set([0, 408, 429, 502, 503, 504])

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false
  if (isApiError(error)) {
    if (RETRYABLE_STATUSES.has(error.status)) return true
    return error.status >= 500
  }
  return false
}

/**
 * Run the request and let it fail rather than holding it back.
 *
 * The library's default is to pause anything started while the browser reports
 * itself offline. Nothing rejects, so a list sits on its skeleton and a Send
 * button spins with no error to read, and the mutation then fires by itself
 * whenever the connection returns, which on a console that sends messages to
 * real people is the wrong surprise. Failing immediately reaches the wording
 * that already exists for it: `NETWORK_UNREACHABLE` is rendered as "Cannot
 * reach the server. Check your connection." with a retry.
 */
const NETWORK_MODE = 'always'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      networkMode: NETWORK_MODE,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
    },
    mutations: {
      networkMode: NETWORK_MODE,
      retry: false,
    },
  },
})

/**
 * Query keys. Use these instead of inline arrays so invalidation is reliable.
 * The first segment is the domain, which is the only thing brand switching and
 * cross-page invalidation need to look at.
 */
export const qk = {
  session: () => ['session'] as const,
  health: () => ['health'] as const,
  /**
   * Who a password reset link belongs to.
   *
   * Its own root rather than a segment under `session`, because `session` is
   * invalidated by prefix in two places and this must not be swept along with
   * it: there is no session on the screen that asks this, and refetching it
   * would fire a request from a page that has already moved on.
   */
  resetSubject: (token: string) => ['passwordReset', token] as const,

  brands: () => ['brands'] as const,
  brand: (id: string) => ['brands', id] as const,
  /** The admin master view: every brand, not the active one. */
  masterBrands: () => ['brands', 'master'] as const,
  /** The two admin-wide lists. Not keyed on the active brand, because they ignore it. */
  masterCampaigns: (filters: Record<string, string | undefined>) => ['campaigns', 'master', filters] as const,
  masterProspects: (filters: Record<string, string | undefined>) => ['prospects', 'master', filters] as const,
  meetingLink: (brandId: string) => ['brands', brandId, 'meeting-link'] as const,
  /** Platform accounts and their memberships. Admin only. */
  users: () => ['users'] as const,
  knowledge: (brandId: string) => ['knowledge', brandId] as const,
  knowledgeVersions: (brandId: string) => ['knowledge', brandId, 'versions'] as const,
  assets: () => ['assets'] as const,
  /** Named prospect lists for the active brand. */
  lists: () => ['lists'] as const,

  prospects: (filters?: Record<string, unknown>) => ['prospects', filters ?? {}] as const,
  prospect: (id: string) => ['prospects', id] as const,
  /** Where one person is active. Under the person's key, so anything that refreshes them refreshes this. */
  prospectChannelRanking: (id: string) => ['prospects', id, 'channel-ranking'] as const,
  importBatch: (id: string) => ['imports', id] as const,
  reviewQueue: () => ['review-queue'] as const,

  methods: () => ['methods'] as const,
  method: (id: string) => ['methods', id] as const,

  campaigns: (filters?: Record<string, unknown>) => ['campaigns', filters ?? {}] as const,
  campaign: (id: string) => ['campaigns', id] as const,
  enrollments: (campaignId: string, filters?: Record<string, unknown>) =>
    ['campaigns', campaignId, 'enrollments', filters ?? {}] as const,

  approvals: (filters?: Record<string, unknown>) => ['approvals', filters ?? {}] as const,

  cards: (filters?: Record<string, unknown>) => ['cards', filters ?? {}] as const,

  conversations: (filters?: Record<string, unknown>) => ['conversations', filters ?? {}] as const,
  conversation: (id: string) => ['conversations', id] as const,

  channelAccounts: () => ['channel-accounts'] as const,
  /**
   * The scout picked for each app, and the accounts that could be one.
   *
   * Under the accounts root on purpose. A scout's sentence says whether its
   * account is paused or kept for reading, so every change to an account that
   * already refreshes the roster has to refresh this too, and sharing the root
   * does that without each of those mutations having to remember it.
   */
  scouts: () => ['channel-accounts', 'scouts'] as const,
  policies: () => ['policies'] as const,
  safetySettings: () => ['safety-settings'] as const,
  integrationSessions: () => ['integration-sessions'] as const,
  brandSendingAccounts: () => ['brand-sending-accounts'] as const,
  engineTokens: () => ['engine-tokens'] as const,
  /** What the device engine has reported, filtered or not. Admin only. */
  engineEvents: (filters?: Record<string, unknown>) => ['engine-events', filters ?? {}] as const,
  /** What each engine last said it can do. Admin only. */
  engineManifests: () => ['engine-manifests'] as const,
  engineSweepRequests: () => ['engine-sweep-requests'] as const,
  /**
   * Device actions a person asked for by hand, filtered or not.
   *
   * The domain segment is what lets running one and cancelling one sweep every
   * filtered variant of the list with `['operator-actions']`, which matters
   * because a new action belongs at the top of a list the caller may be holding
   * under a narrower filter than the one it was created under.
   */
  operatorActions: (filters?: Record<string, unknown>) => ['operator-actions', filters ?? {}] as const,
  mailboxes: () => ['mailboxes'] as const,
  suppressions: (filters?: Record<string, unknown>) => ['suppressions', filters ?? {}] as const,

  analyticsQuota: (filters?: Record<string, unknown>) => ['analytics', 'quota', filters ?? {}] as const,
  analyticsCampaigns: (filters?: Record<string, unknown>) =>
    ['analytics', 'campaigns', filters ?? {}] as const,
  analyticsAccounts: () => ['analytics', 'accounts'] as const,
  /** Campaign performance across every brand. Admin only. */
  analyticsCrossBrand: (filters?: Record<string, unknown>) =>
    ['analytics', 'cross-brand', filters ?? {}] as const,
  /** One campaign's A/B arms. */
  analyticsAb: (campaignId: string, filters?: Record<string, unknown>) =>
    ['analytics', 'ab', campaignId, filters ?? {}] as const,
  analyticsEnrichment: () => ['analytics', 'enrichment'] as const,
  analyticsReconciliation: () => ['analytics', 'reconciliation'] as const,
} as const

/** Keys that survive a brand switch. Everything else is scoped to the active brand. */
// A password reset happens with nobody signed in, so there is no brand for it
// to be scoped to and nothing for a brand switch to invalidate.
//
// The prospects pages' reads (`['prospecting', ...]`, pages/prospecting/shared/api.ts)
// are not scoped by the workspace either: each names its own brands with
// `?brand=`, which is part of its key. A workspace switch moves the brand
// filter to the brands linked to it, and that new filter is a new key, so
// sweeping the old entries would only refetch what nothing reads.
const BRAND_INDEPENDENT_ROOTS = new Set(['session', 'health', 'passwordReset', 'prospecting'])

/**
 * Drop and refetch everything the active brand scopes.
 *
 * Inactive queries are removed outright rather than invalidated, so a page the
 * operator returns to later can never paint the previous brand's numbers for a
 * frame before its refetch lands.
 */
export async function invalidateBrandScopedQueries(client: QueryClient = queryClient): Promise<void> {
  const isBrandScoped = (key: readonly unknown[]): boolean =>
    typeof key[0] !== 'string' || !BRAND_INDEPENDENT_ROOTS.has(key[0])

  client.removeQueries({
    type: 'inactive',
    predicate: (query) => isBrandScoped(query.queryKey),
  })
  await client.invalidateQueries({
    predicate: (query) => isBrandScoped(query.queryKey),
  })
}
